/**
 * Name → alias substitution for the editable PlantUML pane.
 *
 * The user can type a relation call with concept *names* in the alias
 * positions, e.g.
 *
 *   Rel_Flow("Become a customer", "Accept employee", "bla bla")
 *
 * Once they pause, this helper finds quoted strings in the first two arg
 * slots of `Rel_*` calls, looks them up by `name.nl` / `name.en` against
 * the live workspace store, and reports a list of replacements
 * (`{from, to, insert}`) suitable for a single CodeMirror transaction.
 *
 * Lookup is case-insensitive and trimmed. Names that don't match anything
 * are left alone — the parser will then flag them, or the user will keep
 * typing.
 */
import type { WorkspaceStore } from "@ae/domain";

export interface Substitution {
  from: number;
  to: number;
  insert: string;
}

export function findNameSubstitutions(doc: string, store: WorkspaceStore): Substitution[] {
  // Build a name index once per call. The store is small enough (≤500
  // concepts in MVP) that a fresh scan is cheap.
  const nameToAlias = new Map<string, string>();
  for (const c of store.listConcepts()) {
    if (c.tombstoneInBranch) continue;
    const alias = `n_${c.id.replace(/-/g, "_")}`;
    for (const v of Object.values(c.name)) {
      const key = v.trim().toLowerCase();
      if (key && !nameToAlias.has(key)) {
        nameToAlias.set(key, alias);
      }
    }
  }
  if (nameToAlias.size === 0) return [];

  const out: Substitution[] = [];
  // Scan line-by-line so we can compute absolute char offsets cleanly.
  let lineStart = 0;
  const lines = doc.split("\n");
  for (const rawLine of lines) {
    const lineEnd = lineStart + rawLine.length;
    // Pattern: `Rel_<Word>(<args>)`. We capture the args portion including
    // its position within the line, then walk through the args to find
    // quoted strings in the first two positions.
    const callMatch = /^(\s*)(Rel_[A-Za-z][\w]*)\s*\(([\s\S]*)\)\s*$/.exec(rawLine);
    if (callMatch) {
      const leading = callMatch[1] ?? "";
      const procName = callMatch[2] ?? "";
      const args = callMatch[3] ?? "";
      // Args start in the line at: leading + procName + `(`
      const argsStartInLine =
        leading.length +
        procName.length +
        rawLine.slice(leading.length + procName.length).indexOf("(") +
        1;
      collectQuotedSubs(args, argsStartInLine + lineStart, nameToAlias, out);
    }
    lineStart = lineEnd + 1; // +1 for the "\n"
  }
  return out;
}

/**
 * Walks comma-separated args and, for the first TWO args only, checks
 * whether the arg is a quoted string. If so and the name resolves, push a
 * substitution that replaces the entire quoted run (including quotes).
 */
function collectQuotedSubs(
  args: string,
  argsAbsStart: number,
  nameToAlias: Map<string, string>,
  out: Substitution[],
): void {
  let i = 0;
  let argIndex = 0;
  while (i < args.length && argIndex < 2) {
    // Skip whitespace.
    while (i < args.length && /\s/.test(args[i] as string)) i++;
    if (i >= args.length) return;

    const ch = args[i];
    if (ch === '"') {
      const quoteStart = i;
      i++; // past opening quote
      let str = "";
      let escaped = false;
      while (i < args.length) {
        const c = args[i] as string;
        if (escaped) {
          str += c;
          escaped = false;
          i++;
          continue;
        }
        if (c === "\\") {
          escaped = true;
          i++;
          continue;
        }
        if (c === '"') {
          break;
        }
        str += c;
        i++;
      }
      if (i >= args.length) return; // unterminated string
      const quoteEnd = i + 1; // include closing quote
      i = quoteEnd;
      const lookup = str.trim().toLowerCase();
      const alias = nameToAlias.get(lookup);
      if (alias) {
        out.push({
          from: argsAbsStart + quoteStart,
          to: argsAbsStart + quoteEnd,
          insert: alias,
        });
      }
    } else {
      // Bare identifier or unexpected token — consume until next comma.
      while (i < args.length && args[i] !== ",") i++;
    }
    // Skip past the comma.
    if (i < args.length && args[i] === ",") {
      i++;
      argIndex++;
    } else {
      break;
    }
  }
}
